import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = [
  'Musique', 'Cinéma', 'Voyages', 'Sport', 'Lecture', 'Cuisine',
  'Art', 'Technologie', 'Nature', 'Photographie', 'Danse', 'Yoga'
];

const personalityQuestions = [
  { id: 'extraversion', label: 'Êtes-vous plutôt extraverti(e) ou introverti(e) ?', min: 'Introverti', max: 'Extraverti' },
  { id: 'aventure', label: 'Aimez-vous l\'aventure et les nouvelles expériences ?', min: 'Routine', max: 'Aventure' },
  { id: 'emotion', label: 'Êtes-vous plutôt rationnel(le) ou émotionnel(le) ?', min: 'Rationnel', max: 'Émotionnel' },
  { id: 'planification', label: 'Préférez-vous planifier ou improviser ?', min: 'Improviser', max: 'Planifier' },
  { id: 'social', label: 'Comment préférez-vous passer votre temps libre ?', min: 'Seul(e)', max: 'En groupe' }
];

const compatibilityQuestions = [
  {
    id: 'weekend',
    question: 'Comment préférez-vous passer un week-end idéal ?',
    options: [
      { id: 'adventure', label: 'Aventure en plein air et découvertes' },
      { id: 'culture', label: 'Musées, expositions et culture' },
      { id: 'relax', label: 'Détente à la maison avec un bon livre/film' },
      { id: 'social', label: 'Sorties entre amis et vie sociale' }
    ]
  },
  {
    id: 'conflict',
    question: 'Comment gérez-vous les conflits dans une relation ?',
    options: [
      { id: 'talk', label: 'Discussion immédiate et franche' },
      { id: 'time', label: 'Prendre du temps pour réfléchir avant de parler' },
      { id: 'compromise', label: 'Recherche rapide de compromis' },
      { id: 'avoid', label: 'Éviter les confrontations directes' }
    ]
  },
  {
    id: 'future',
    question: 'Quelle est votre vision de l\'avenir à deux ?',
    options: [
      { id: 'family', label: 'Fonder une famille et avoir des enfants' },
      { id: 'career', label: 'Se concentrer sur nos carrières respectives' },
      { id: 'travel', label: 'Voyager et explorer le monde ensemble' },
      { id: 'simple', label: 'Une vie simple et tranquille à deux' }
    ]
  },
  {
    id: 'communication',
    question: 'Quel est votre style de communication préféré ?',
    options: [
      { id: 'direct', label: 'Direct et sans détour' },
      { id: 'diplomatic', label: 'Diplomatique et nuancé' },
      { id: 'affectionate', label: 'Affectueux et expressif' },
      { id: 'practical', label: 'Pratique et factuel' }
    ]
  },
  {
    id: 'lifestyle',
    question: 'Quel mode de vie vous correspond le mieux ?',
    options: [
      { id: 'active', label: 'Actif et sportif' },
      { id: 'cultural', label: 'Culturel et intellectuel' },
      { id: 'homebody', label: 'Casanier et confortable' },
      { id: 'spontaneous', label: 'Spontané et imprévisible' }
    ]
  },
  {
    id: 'values',
    question: 'Quelle valeur est la plus importante pour vous dans une relation ?',
    options: [
      { id: 'trust', label: 'La confiance et l\'honnêteté' },
      { id: 'passion', label: 'La passion et l\'attraction' },
      { id: 'respect', label: 'Le respect mutuel' },
      { id: 'complicity', label: 'La complicité et l\'humour' }
    ]
  },
  {
    id: 'expression',
    question: 'Comment exprimez-vous votre affection ?',
    options: [
      { id: 'words', label: 'Par des mots doux et compliments' },
      { id: 'actions', label: 'Par des gestes et des attentions' },
      { id: 'time', label: 'En passant du temps de qualité ensemble' },
      { id: 'gifts', label: 'Par des cadeaux et surprises' }
    ]
  },
  {
    id: 'social_life',
    question: 'Quelle importance accordez-vous à la vie sociale en couple ?',
    options: [
      { id: 'very_important', label: 'Très importante - Sorties fréquentes avec amis' },
      { id: 'balanced', label: 'Équilibrée - Mix entre sorties et moments à deux' },
      { id: 'selective', label: 'Sélective - Sorties occasionnelles ciblées' },
      { id: 'intimate', label: 'Intime - Préférence pour les moments à deux' }
    ]
  }
];

export default function Questionnaire() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [personality, setPersonality] = useState({});
  const [compatibilityAnswers, setCompatibilityAnswers] = useState({});
  const [bio, setBio] = useState('');
  const [typePreference, setTypePreference] = useState('');
  const [loading, setLoading] = useState(false);

  const toggleInterest = (interest) => {
    if (selectedInterests.includes(interest)) {
      setSelectedInterests(selectedInterests.filter(i => i !== interest));
    } else {
      setSelectedInterests([...selectedInterests, interest]);
    }
  };

  const handlePersonalityChange = (id, value) => {
    setPersonality({ ...personality, [id]: value });
  };

  const handleCompatibilityAnswer = (questionId, answerId) => {
    setCompatibilityAnswers({ ...compatibilityAnswers, [questionId]: answerId });
  };

  const handleSubmit = async () => {
    if (selectedInterests.length < 3) {
      toast.error('Veuillez sélectionner au moins 3 intérêts');
      return;
    }

    // Vérifier que toutes les questions de compatibilité sont répondues
    const allAnswered = compatibilityQuestions.every(q => compatibilityAnswers[q.id]);
    if (!allAnswered) {
      toast.error('Veuillez répondre à toutes les questions de compatibilité');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/profile/me`,
        {
          interests: selectedInterests,
          personality_traits: {
            ...personality,
            compatibility_answers: compatibilityAnswers
          },
          bio: bio,
          type_preference: typePreference,
          beauty_score: 50
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Profil complété !');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="glass-card p-8">
          <div className="text-center mb-8">
            <Sparkles className="w-12 h-12 mx-auto mb-4 text-pink-500" />
            <h2 className="text-3xl font-bold gradient-text mb-2">
              Parlez-nous de vous
            </h2>
            <p className="text-gray-600">
              Cela nous aidera à trouver votre match parfait
            </p>
          </div>

          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Question {step}/4</span>
              <span className="text-sm font-medium text-pink-600">{Math.round((step / 4) * 100)}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full">
              <div
                className="h-full bg-gradient-to-r from-pink-500 to-orange-400 rounded-full"
                style={{ width: `${(step / 4) * 100}%`, transition: 'width 0.3s' }}
              />
            </div>
          </div>

          {/* Step 1: Interests */}
          {step === 1 && (
            <div data-testid="questionnaire-step-1">
              <h3 className="text-xl font-bold mb-4 text-gray-800">Quels sont vos intérêts ?</h3>
              <p className="text-gray-600 mb-6">Sélectionnez au moins 3 intérêts</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
                {interests.map(interest => (
                  <button
                    key={interest}
                    data-testid={`interest-${interest.toLowerCase()}`}
                    onClick={() => toggleInterest(interest)}
                    className={`p-4 rounded-lg border-2 font-medium ${
                      selectedInterests.includes(interest)
                        ? 'border-pink-500 bg-pink-50 text-pink-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-pink-300'
                    }`}
                    style={{ transition: 'all 0.2s' }}
                  >
                    {interest}
                  </button>
                ))}
              </div>
              <button
                data-testid="questionnaire-next-btn"
                onClick={() => setStep(2)}
                disabled={selectedInterests.length < 3}
                className="w-full btn-primary py-4"
              >
                Suivant
              </button>
            </div>
          )}

          {/* Step 2: Personality */}
          {step === 2 && (
            <div data-testid="questionnaire-step-2">
              <h3 className="text-xl font-bold mb-6 text-gray-800">Votre personnalité</h3>
              <div className="space-y-8 mb-8">
                {personalityQuestions.map(q => (
                  <div key={q.id}>
                    <label className="block text-gray-700 font-medium mb-3">{q.label}</label>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-500 min-w-[80px]">{q.min}</span>
                      <input
                        data-testid={`personality-${q.id}`}
                        type="range"
                        min="0"
                        max="100"
                        value={personality[q.id] || 50}
                        onChange={(e) => handlePersonalityChange(q.id, parseInt(e.target.value))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #ff6b9d 0%, #ff6b9d ${personality[q.id] || 50}%, #e5e7eb ${personality[q.id] || 50}%, #e5e7eb 100%)`
                        }}
                      />
                      <span className="text-xs text-gray-500 min-w-[80px] text-right">{q.max}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-4">
                <button
                  data-testid="questionnaire-back-btn"
                  onClick={() => setStep(1)}
                  className="flex-1 btn-secondary py-4"
                >
                  Retour
                </button>
                <button
                  data-testid="questionnaire-next-btn-2"
                  onClick={() => setStep(3)}
                  className="flex-1 btn-primary py-4"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Compatibility Questions */}
          {step === 3 && (
            <div data-testid="questionnaire-step-3">
              <h3 className="text-xl font-bold mb-6 text-gray-800">Questions de compatibilité</h3>
              <p className="text-gray-600 mb-6">Répondez à ces questions pour améliorer votre matching</p>
              
              <div className="space-y-6 mb-8">
                {compatibilityQuestions.map((q, index) => (
                  <div key={q.id} className="glass-card p-5">
                    <h4 className="font-semibold text-gray-800 mb-4">
                      {index + 1}. {q.question}
                    </h4>
                    <div className="space-y-2">
                      {q.options.map((option) => (
                        <label
                          key={option.id}
                          className={`flex items-center p-3 rounded-lg border-2 cursor-pointer ${
                            compatibilityAnswers[q.id] === option.id
                              ? 'border-pink-500 bg-pink-50'
                              : 'border-gray-200 hover:border-pink-300 bg-white'
                          }`}
                          style={{ transition: 'all 0.2s' }}
                        >
                          <input
                            type="radio"
                            name={q.id}
                            value={option.id}
                            checked={compatibilityAnswers[q.id] === option.id}
                            onChange={() => handleCompatibilityAnswer(q.id, option.id)}
                            className="w-4 h-4 text-pink-600 mr-3"
                            data-testid={`compatibility-${q.id}-${option.id}`}
                          />
                          <span className="text-gray-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <button
                  data-testid="questionnaire-back-btn-2"
                  onClick={() => setStep(2)}
                  className="flex-1 btn-secondary py-4"
                >
                  Retour
                </button>
                <button
                  data-testid="questionnaire-next-btn-3"
                  onClick={() => setStep(4)}
                  className="flex-1 btn-primary py-4"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Bio & Preferences */}
          {step === 4 && (
            <div data-testid="questionnaire-step-4">
              <h3 className="text-xl font-bold mb-6 text-gray-800">Finalisez votre profil</h3>
              
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">Bio</label>
                <textarea
                  data-testid="questionnaire-bio-input"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  rows="4"
                  placeholder="Parlez-nous de vous..."
                />
              </div>

              <div className="mb-8">
                <label className="block text-gray-700 font-medium mb-2">Type de personne recherchée</label>
                <input
                  data-testid="questionnaire-type-input"
                  type="text"
                  value={typePreference}
                  onChange={(e) => setTypePreference(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="Ex: Quelqu'un d'aventureux, attentionné..."
                />
              </div>

              <div className="flex gap-4">
                <button
                  data-testid="questionnaire-back-btn-3"
                  onClick={() => setStep(3)}
                  className="flex-1 btn-secondary py-4"
                >
                  Retour
                </button>
                <button
                  data-testid="questionnaire-submit-btn"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 btn-primary py-4"
                >
                  {loading ? 'Enregistrement...' : 'Terminer'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
